import {ReactElement} from "react";
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Title from "../../components/Title";
import TransactionProgress from "./TransactionProgressPage";
import formatDateTime from "../../components/TimeFormat";
import {useFindByFutureTransactionId} from "../../application/FutureTransactionService";
import {GetDataApi} from "../../components/types/GetDataApi";
import {FutureTransaction} from "../../domain/transaction/FutureTransaction";
import {TransactionType} from "../../domain/transaction/TransactionType";
import {Button, Tooltip} from "@mui/material";
import {ErrorOutlineRounded, HistoryRounded} from "@mui/icons-material";
import {Link} from "react-router-dom";
import RolesRetriever from "../../authorization/RolesRetreiver";
import formatNumber from "../../components/NumberFormat";

/*
{

    "externalTransactionId": "B003066260008",
    "transactionCode": "SELL_FUTURE",
    "tradeDate": "2022-09-29",
    "valueDate": "2022-09-29",
    "originalFileName": "future.tsv",
    "originalFileRow": 1,
    "counterpartBicCode": "UBSWGB2L",
    "brokerShort": "UBS GB",
    "brokerName": "UBS Warburg (UBS AG), London",
    "brokerId": "UBSWGB2L",
    "brokerBicCode": "UBSWGB2L",
    "securityId": "CAC DEC22",
    "instrumentShort": "CAC DEC22",
    "instrumentName": "CAC40 10 EURO FUT Dec22",
    "instrumentType": "Future",
    "bloombergId": "CFZ2 Index",
    "currency": "EUR",
    "quantity": 26,
    "price": 1794,
    "fee1": 780,
    "quoteFactor": null
  }
 */
export default function FutureTransactionPage(): ReactElement {
    const transaction: GetDataApi<FutureTransaction> = useFindByFutureTransactionId();

    if (transaction.loading) {
        return (<></>);
    } else {
        return (
            <Container maxWidth={false} sx={{mt: 4, mb: 4, px: 2}}>
                {<Toolbar/>}
                <Grid
                    container
                    spacing={2}
                    sx={{
                        justifyItems: "center",
                        alignItems: "center"
                    }}>
                    <Grid size={{ xs: 12, md: 9, lg: 9 }}>
                        <Title>Future Transaction {transaction.data.transactionId} </Title>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2, lg: 2 }}>
                        <RolesRetriever groups={ ["super"] }>
                                <Button variant='outlined'
                                    startIcon={ <ErrorOutlineRounded/> }
                                    sx={{ "whiteSpace": "nowrap" }}
                                    component={Link} to={ "/issues/inbound/transaction/" + transaction.data.assetManager + "/" + transaction.data.externalTransactionId }>
                                Related Issues
                            </Button>
                        </RolesRetriever>
                    </Grid>
                    <Grid size={{ xs: 12, md: 1, lg: 1 }}>
                        <RolesRetriever groups={ ["super"] }>
                                <Tooltip title={ "Related Issue History" } placement={"top"}>
                                <Button variant='outlined'
                                        startIcon={ <HistoryRounded/> }
                                        component={Link} to={ "/audit/events/FU/" + transaction.data.transactionId }>
                                    History
                                </Button>
                            </Tooltip>
                        </RolesRetriever>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <TransactionProgress assetManager={transaction.data.assetManager}
                                             transactionType={TransactionType.FU}
                                             transactionId={transaction.data.transactionId}/>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>TransactionId</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.transactionId}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Last Processed</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}> {formatDateTime(transaction.data.lastProcessed)}</Grid>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Asset Manager Transaction</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.externalTransactionId}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Asset Manager</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.assetManager}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Mandate</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.mandate}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Fund </Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.fundShort}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Fund Full</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.fund}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Portfolio</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.portfolio}</Grid>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Counterpart</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.counterpartShort}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Counterpart Full</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.counterpartName}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Counterpart Id</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.counterpartId}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Counterpart BicCode</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.counterpartBicCode}</Grid>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Broker</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.brokerShort}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Broker Full</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.brokerName}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Broker Id</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.brokerId}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Broker BicCode</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.brokerBicCode}</Grid>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Asset Manager Instrument</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.securityId}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>SCD Instrument</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.instrumentShort}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Instrument Name</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.instrumentName}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Instrument Type</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.instrumentType}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Bloomberg Code</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.bloombergId}</Grid>
                    </Grid>
                    <Grid container size={{ xs: 12 }} sx={{ border: 2, borderRadius: "6px", borderColor: "primary.main", mb: 2 }}>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Currency</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.currency}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Transaction Code</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.transactionCode}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Quantity</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{formatNumber(transaction.data.quantity)}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Price</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{formatNumber(transaction.data.price)}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Fee 1</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{formatNumber(transaction.data.fee1)}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Quote Factor</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.quoteFactor}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Trade Date</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.tradeDate}</Grid>
                        <Grid size={{ xs: 4, md: 4, lg: 4 }} sx={{ padding: 1 }}>Value Date</Grid>
                        <Grid size={{ xs: 8, md: 8, lg: 8 }} sx={{ padding: 1 }}>{transaction.data.valueDate}</Grid>
                    </Grid>
                </Grid>
            </Container>
        );
    }
}
